import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Wallet, X, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { useUIStore } from '../../stores/useUIStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

const RATES: Record<string, number> = {
  ARS: 7.499,
  MXN: 0.092,
};

const TIERS = [
  { v: 800, b: 0, label: 'Base' },
  { v: 2400, b: 5, label: '+5%' },
  { v: 4500, b: 10, label: '+10%' },
  { v: 12500, b: 15, label: '+15%' },
];

const snap = (n: number) => {
  for (const [t, r] of [
    [800, 120],
    [2400, 160],
    [4500, 190],
    [12500, 220],
  ]) {
    if (Math.abs(n - t) <= r) return t;
  }
  return n;
};

export const DepositModal: React.FC = () => {
  const isOpen = useUIStore((s) => s.isDepositModalOpen);
  const initialAmount = useUIStore((s) => s.depositInitialAmount);
  const close = useUIStore((s) => s.closeDepositModal);
  const openAuth = useUIStore((s) => s.openAuthModal);
  const navigateTo = useUIStore((s) => s.navigateTo);
  const currency = useCurrencyStore((s) => s.currency);
  const authStoreToken = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);

  const [vbucks, setVbucks] = useState(initialAmount || 2400);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    unlockScroll('deposit-modal');
    setTimeout(() => {
      setIsClosing(false);
      close();
    }, 160);
  }, [isClosing, close]);

  useEffect(() => {
    if (isOpen) {
      setVbucks(initialAmount || 2400);
      setErrorMessage(null);
      setIsSubmitting(false);
      setIsClosing(false);
      lockScroll('deposit-modal');
    } else {
      unlockScroll('deposit-modal');
    }
    return () => {
      unlockScroll('deposit-modal');
    };
  }, [isOpen, initialAmount]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  const activeTier = useMemo(() => {
    return [...TIERS].reverse().find((x) => vbucks >= x.v) || TIERS[0];
  }, [vbucks]);

  const extra = Math.round((vbucks * activeTier.b) / 100);
  const totalCoins = vbucks + extra;
  const rate = RATES[currency] || 7.499;
  const totalPrice = Math.round(vbucks * rate);

  const handlePay = async () => {
    if (isSubmitting) return;
    setErrorMessage(null);

    const token =
      authStoreToken ||
      localStorage.getItem('fn_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token');

    if (!token) {
      close();
      openAuth('login');
      return;
    }

    setIsSubmitting(true);

    try {
      // Base vbucks amount (800 to 12500) expected by backend schema
      const safeVbucks = Math.min(12500, Math.max(800, Number(vbucks) || 800));

      const res = await fetch('/api/wallet/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token.trim()}`,
        },
        body: JSON.stringify({
          vbucks: safeVbucks,
          currency: currency === 'MXN' ? 'MXN' : 'ARS',
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 401) {
          logout();
          close();
          openAuth('login');
          return;
        }
        throw new Error(
          data.detail ||
          data.message ||
          'Error al generar el pedido de recarga. Intenta nuevamente.'
        );
      }

      if (!data.order_code) {
        throw new Error('La respuesta del servidor no incluyó el código de pedido.');
      }

      // Persist last order code for resilient OrderPage recovery
      try {
        localStorage.setItem(
          'fn_last_order',
          JSON.stringify({
            order_code: data.order_code,
            created_at: new Date().toISOString(),
          })
        );
      } catch {}

      // Close modal before redirecting
      close();

      const targetUrl =
        data.redirect_url || `/order.html?code=${encodeURIComponent(data.order_code)}`;

      if (typeof navigateTo === 'function') {
        navigateTo('order', targetUrl);
      } else {
        window.location.href = targetUrl;
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Error inesperado al conectar con el servidor.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div
      className={`fixed inset-0 z-[200000] soft-backdrop flex items-center justify-center p-4 overscroll-contain select-none ${
        isClosing ? 'anim-backdrop-out' : 'anim-backdrop-in'
      }`}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="deposit-modal-title"
    >
      <div
        className={`w-full max-w-[460px] bg-white text-[#06173f] p-5 sm:p-6 space-y-5 border-2 border-[var(--line)] shadow-2xl relative ${
          isClosing ? 'anim-modal-out' : 'anim-modal-in'
        }`}
        style={{
          clipPath:
            'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 bg-[var(--ice)] text-[var(--royal)] flex items-center justify-center font-bold text-lg shrink-0"
              style={{
                clipPath:
                  'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
              }}
            >
              <Wallet className="w-5 h-5 text-[var(--royal)]" />
            </div>
            <div>
              <h2
                id="deposit-modal-title"
                className="text-lg font-black uppercase text-[#06173f] font-heading tracking-wide leading-tight"
              >
                CARGAR G-COINS
              </h2>
              <p className="text-xs text-[var(--soft)] font-medium">
                Selecciona la cantidad que deseas recargar
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center border-2 border-[#1447e6] text-[#1447e6] hover:bg-blue-50 transition cursor-pointer"
            style={{
              clipPath:
                'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
            }}
            aria-label="Cerrar modal"
          >
            <X className="w-4 h-4 stroke-[2.5]" />
          </button>
        </div>

        {/* Selector de G-Coins */}
        <div className="select-none space-y-2">
          <label className="text-[11px] font-bold text-[var(--soft)] uppercase tracking-wider block font-heading">
            CANTIDAD DE G-COINS
          </label>

          <div className="flex items-baseline gap-2">
            <span className="text-3xl sm:text-4xl font-black italic tracking-tight font-heading text-[#06173f]">
              {vbucks.toLocaleString('es-AR')}
            </span>
            {extra > 0 && (
              <span
                className="px-2 py-0.5 text-xs font-black font-heading text-[#1447e6] bg-[#eff6ff] border border-[#bfdbfe]"
                style={{
                  clipPath:
                    'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                }}
              >
                +{extra.toLocaleString('es-AR')} extra
              </span>
            )}
          </div>

          {/* Barra Slider Nativa 1:1 con Shimmer en 12.500 */}
          <div className="bar">
            <i
              className={vbucks >= 12500 ? 'mx' : ''}
              style={{ width: `calc(11px + (100% - 22px) * ${(vbucks - 800) / 11700})` }}
            />
            <input
              id="deposit-rg"
              className="rng"
              type="range"
              min="800"
              max="12500"
              step="50"
              value={vbucks}
              aria-valuetext={`${vbucks.toLocaleString('es-AR')} G-Coins`}
              onChange={(e) => setVbucks(snap(Number(e.target.value) || 800))}
            />
          </div>

          {/* Presets 2x2 */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            {TIERS.map((tier) => {
              const isSelected = vbucks === tier.v;
              return (
                <button
                  key={tier.v}
                  type="button"
                  onClick={() => setVbucks(tier.v)}
                  className={`flex items-center justify-between px-3.5 py-2.5 transition font-heading font-black text-sm ${
                    isSelected
                      ? 'bg-[#1447e6] text-white shadow-md'
                      : 'bg-[#f1f5f9] text-[#06173f] hover:bg-[#e2e8f0]'
                  }`}
                  style={{
                    clipPath:
                      'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                  }}
                >
                  <span>{tier.v.toLocaleString('es-AR')}</span>
                  <span
                    className={`text-xs font-semibold ${
                      isSelected ? 'text-blue-100' : 'text-[var(--soft)]'
                    }`}
                  >
                    {tier.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tarjeta de Resumen */}
        <div
          className="p-4 bg-white border-2 border-[var(--line)] space-y-2.5"
          style={{
            clipPath:
              'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
          }}
        >
          <div className="flex items-center justify-between text-xs sm:text-sm">
            <span className="font-bold text-[var(--soft)]">
              Total a recibir en tu cuenta
            </span>
            <span className="text-base sm:text-lg font-black text-[#1447e6] font-heading">
              {totalCoins.toLocaleString('es-AR')} G-Coins
            </span>
          </div>

          <div className="flex items-center justify-between border-t border-[var(--line)] pt-2.5">
            <span className="text-xs sm:text-sm font-bold text-[var(--soft)]">
              Total a pagar
            </span>
            <span className="text-base sm:text-lg font-black text-[#06173f] font-heading">
              ${totalPrice.toLocaleString('es-AR')} {currency}
            </span>
          </div>
        </div>

        {/* Error Feedback */}
        {errorMessage && (
          <div
            className="p-3 bg-red-50 border border-red-200 text-red-600 text-xs flex items-center gap-2 animate-fadeIn"
            style={{ clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)' }}
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span className="font-semibold">{errorMessage}</span>
          </div>
        )}

        {/* Botón de Pago */}
        <button
          type="button"
          onClick={handlePay}
          disabled={isSubmitting}
          className={`w-full ${
            isSubmitting ? 'bg-[#1447e6]/70 cursor-not-allowed' : 'bg-[#1447e6] hover:bg-[#0a2a9c] cursor-pointer'
          } text-white font-heading font-black py-3 sm:py-3.5 uppercase tracking-wider text-sm transition flex items-center justify-center gap-2 shadow-lg`}
          style={{
            clipPath:
              'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
          }}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin stroke-[2.5]" />
              <span>Creando Pedido...</span>
            </>
          ) : (
            <>
              <span>Ir al Pago</span>
              <ArrowRight className="w-4 h-4 stroke-[2.5]" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DepositModal;
